import React, { type ReactNode } from 'react';
import { TinyColor } from '@ctrl/tinycolor';
import { useTheme } from '@emotion/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import * as RadixDropdown from '@radix-ui/react-dropdown-menu';
import { cs } from '@bbkAdminUtils/css-utils';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';

const hexToRgb = (hex: string | undefined): string => {
  if (!hex) return '0 0 0';
  const color = new TinyColor(hex);
  const rgb = color.toRgb();
  return `${rgb.r} ${rgb.g} ${rgb.b}`;
};

export default function Dropdown<T>({
  id,
  selected,
  triggerClassName,
  contentClassName,
  itemClassName,
  trigger,
  items,
  renderItem,
  renderItemMobile,
  itemKeyName,
  onChange,
}: Readonly<{
  id: string;
  selected: T;
  items: T[];
  renderItem: (item: T, isSelected?: boolean) => ReactNode;
  renderItemMobile?: (item: T, isSelected?: boolean) => ReactNode;
  itemKeyName: keyof T;
  onChange: (item: T) => void;
  triggerClassName?: string;
  contentClassName?: string;
  itemClassName?: string;
  trigger: ReactNode;
}>) {
  const theme = useTheme() as EmotionTheme;
  const _onSelectedChange = (item: T) => {
    onChange(item);
  };

  return (
    <>
      <div className="@sm:tw-hidden">
        <MobileDropdown
          id={id}
          selected={selected}
          items={items}
          renderItem={renderItemMobile ?? renderItem}
          itemKeyName={itemKeyName}
          onChange={_onSelectedChange}
        />
      </div>
      <div className="tw-hidden @sm:tw-block">
        <RadixDropdown.Root>
          <RadixDropdown.Trigger
            id={id}
            data-testid={`dropdown--desktop-${id}`}
            className={triggerClassName}
          >
            {trigger}
          </RadixDropdown.Trigger>

          <RadixDropdown.Portal>
            <RadixDropdown.Content
              className={cs(
                contentClassName,
                'scrollbar-w-none tw-z-[9999] tw-max-h-[var(--radix-dropdown-menu-content-available-height)] tw-animate-slideUpAndFadeIn tw-overflow-y-scroll'
              )}
              collisionPadding={20}
              sideOffset={4}
              style={
                {
                  '--color-pricingtable-brand': hexToRgb(
                    theme.buttonBackgroundColor
                  ),
                  '--color-pricingtable-background': hexToRgb(theme.background),
                  '--color-pricingtable-accent': hexToRgb(
                    theme.buttonBackgroundColor
                  ),
                  '--color-pricingtable-text-base': hexToRgb(theme.textColor),
                  '--color-pricingtable-text-contrast': hexToRgb(
                    theme.buttonTextColor
                  ),
                  '--pricingtable-border-radius': `${theme.buttonBorderRadius}px`,
                  '--opacity-pricingtable-background': '1',
                } as React.CSSProperties
              }
            >
              <div className="tw-bg-[color-mix(in_srgb,var(--pricing-page-text-font-text-color)_10%,transparent)]">
                {items.map((item) => {
                  return (
                    <RadixDropdown.Item
                      data-testid={`option--desktop-${item[itemKeyName]}`}
                      key={item[itemKeyName] as string}
                      onSelect={() => _onSelectedChange(item)}
                      className={itemClassName}
                    >
                      {renderItem(
                        item,
                        selected[itemKeyName] === item[itemKeyName]
                      )}
                    </RadixDropdown.Item>
                  );
                })}
              </div>
            </RadixDropdown.Content>
          </RadixDropdown.Portal>
        </RadixDropdown.Root>
      </div>
    </>
  );
}

const MobileDropdown = <T,>({
  id,
  selected,
  items,
  renderItem,
  itemKeyName,
  onChange,
}: Readonly<{
  id: string;
  selected: T;
  items: T[];
  renderItem: (item: T, isSelected?: boolean) => ReactNode;
  itemKeyName: keyof T;
  onChange: (item: T) => void;
}>) => {
  const _onSelectedChange = (item: string) => {
    const selectedItem = items.find((i) => i[itemKeyName] === item);
    onChange(selectedItem as T);
  };

  return (
    <div className="tw-relative tw-w-full">
      <select
        id={id}
        data-testid={`dropdown--mobile-${id}`}
        className={cs(
          'tw-inline-flex tw-w-full tw-cursor-pointer tw-appearance-none tw-justify-center tw-rounded-pricingtable-border-radius tw-border-none',
          'tw-font-medium tw-transition-all tw-duration-75',
          'tw-py-4 tw-pl-7 tw-pr-12 tw-text-left tw-text-lg',
          '@sm:tw-px-4 @sm:tw-py-3 @sm:tw-text-sm',
          'focus:tw-outline-none',
          'tw-bg-[color-mix(in_srgb,var(--pricing-page-text-font-text-color)_10%,transparent)] tw-text-[color-mix(in_srgb,var(--pricing-page-text-font-text-color)_80%,transparent)]',
          'hover:tw-bg-[color-mix(in_srgb,var(--pricing-page-text-font-text-color)_15%,transparent)]'
        )}
        value={selected[itemKeyName] as string}
        onChange={(e) => _onSelectedChange(e.target.value)}
      >
        {items.map((item) => (
          <option
            key={item[itemKeyName] as string}
            value={item[itemKeyName] as string[]}
            data-testid={`option--mobile-${item[itemKeyName]}`}
          >
            {renderItem(item, selected[itemKeyName] === item[itemKeyName])}
          </option>
        ))}
      </select>
      <ChevronDownIcon
        className="tw-pointer-events-none tw-absolute tw-right-4 tw-top-1/2 tw-h-5 tw-w-5 tw--translate-y-1/2 tw-text-[color-mix(in_srgb,var(--pricing-page-text-font-text-color)_80%,transparent)]"
        aria-hidden="true"
      />
    </div>
  );
};
